import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { addMessageToChat } from '../../../chatSlice';
import { MessageProps } from './components/Message';
import './styles/DialogsPage.scss';
import { useRenameChat } from '../../contexts/RenameChatContext';
import ChatHeader from './components/ChatHeader';
import MessagesList from './components/MessagesList';
import ChatInput from './components/ChatInput';
import RenameChatDialog from './components/RenameChatDialog';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { ChatApiService } from '../../utils/chatApiUtils';
import phrases from './phrases.json';
import SpecialityDialog from './components/SpecialityDialog';
import AudioUploadDialog from './components/AudioUploadDialog';
import ProtocolSelectionDialog from './components/ProtocolSelectionDialog';
import ProtocolInstrumentsDialog from './components/ProtocolInstrumentsDialog';
import { apiPost, generateProtocolFromChat, GenerateProtocolFromChatRequest, searchSpecialities } from '../../utils/apiUtils';
import { hasProtocolsAccess } from '../../utils/accessUtils';
import { UserProtocol, ProtocolMessage } from '../../types/protocols';
import { useChatSync, useChatStream } from '../../hooks/useChatSync';
import { useLocation } from 'react-router-dom';
import { getCurrentISOString } from '../../utils/timeUtils';
import { useSnackbar } from 'notistack';
import { useI18n } from '../../contexts/I18nContext';

const AZURE_SPEECH_KEY = 'ff2ecd2704c4410db048547c063dd999';
const AZURE_SPEECH_REGION = 'eastus2';

const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
};

const DialogsPage: React.FC = (): React.ReactElement => {
    const location = useLocation();
    const urlProtocolId = new URLSearchParams(location.search).get('protocolId') ?? '';

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
    const prevChatIdRef = useRef<string | null>(null);
    const prevMessagesLengthRef = useRef<number>(0);
    const [inputValue, setInputValue] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [isProtocolButtonActive, setIsProtocolButtonActive] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isGeneratingProtocol, setIsGeneratingProtocol] = useState(false);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<BlobPart[]>([]);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const isRecordingCancelledRef = useRef<boolean>(false);
    const isAzureRecordingCancelledRef = useRef<boolean>(false);
    const [recognizedSegments, setRecognizedSegments] = useState<{ text: string, speakerId: string }[]>([]);
    const [interimText, setInterimText] = useState<string>('');
    const [isSpecialityDialogOpen, setIsSpecialityDialogOpen] = useState(false);
    const [, setChoosenProtocol] = useState<string | null>(null);
    const [availableSpecialities, setAvailableSpecialities] = useState<any[]>([]);
    const [isWaitingForFinalResults, setIsWaitingForFinalResults] = useState(false);
    const [isAudioUploadDialogOpen, setIsAudioUploadDialogOpen] = useState(false);
    const [isProtocolSelectionDialogOpen, setIsProtocolSelectionDialogOpen] = useState(false);
    const [, setSelectedProtocolForGeneration] = useState<UserProtocol | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasGeneratedProtocols, setHasGeneratedProtocols] = useState(false);
    const [instrumentsDialogOpen, setInstrumentsDialogOpen] = useState(false);
    const [instrumentsDialogProtocol, setInstrumentsDialogProtocol] = useState<ProtocolMessage | null>(null);
    const [instrumentsDialogProtocolId, setInstrumentsDialogProtocolId] = useState<string>('');
    const [instrumentsDialogAnchor, setInstrumentsDialogAnchor] = useState<{ top: number; left: number } | null>(null);

    const dispatch = useDispatch();
    const { chats, selectedChatId } = useSelector((state: RootState) => state.chat);
    const user = useSelector((state: RootState) => state.user);
    const recognitionType = user?.recognitionType || 'qwen';
    const { isRenameDialogOpen, renameChatId, renameChatTitle, closeRenameDialog, openRenameDialog } = useRenameChat();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useI18n();

    // Используем новые хуки для синхронизации
    const {
        createChat,
        syncMessage,
        updateChat,
        isOnline,
        isSyncing
    } = useChatSync();

    // Потоки истории сообщений временно не используются

    const { isConnected } = useChatStream(selectedChatId);

    const selectedChat = chats.find((chat: { id: string }) => chat.id === selectedChatId);

    // Утилитарная функция для обновления названия чата на сервере
    const updateChatTitleOnServer = useCallback(async (chatId: string, newTitle: string) => {
        if (isOnline) {
            try {
                await updateChat(chatId, { title: newTitle });
                console.log('Название чата обновлено на сервере:', newTitle);
            } catch (error) {
                console.warn('Не удалось обновить название чата на сервере:', error);
            }
        }
    }, [isOnline, updateChat]);

    const recognizerRef = useRef<SpeechSDK.ConversationTranscriber | null>(null);
    const recognizedSegmentsRef = useRef<{ text: string, speakerId: string }[]>([]);
    const pendingSegmentsRef = useRef<{ text: string, speakerId: string }[]>([]);
    const savedSegmentsCountRef = useRef<number>(0);

    // Обновляем ref при изменении recognizedSegments
    useEffect(() => {
        recognizedSegmentsRef.current = recognizedSegments;
    }, [recognizedSegments]);



    // При смене чата сбрасываем автоскролл и скроллим вниз
    useEffect(() => {
        if (selectedChatId !== prevChatIdRef.current) {
            prevChatIdRef.current = selectedChatId;
            setShouldAutoScroll(true);
            prevMessagesLengthRef.current = selectedChat?.messages.length || 0;
            // Скроллим вниз при первой загрузке чата
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
            }, 0);
        }
    }, [selectedChatId, selectedChat]);

    // Скроллим вниз только при добавлении новых сообщений и если включен автоскролл
    useLayoutEffect(() => {
        if (!selectedChat || !isInitialized) return;

        const currentLength = selectedChat.messages.length;
        const prevLength = prevMessagesLengthRef.current;

        // Скроллим только если добавились новые сообщения и включен автоскролл
        if (currentLength > prevLength && shouldAutoScroll) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }

        prevMessagesLengthRef.current = currentLength;
    }, [selectedChat?.messages.length, shouldAutoScroll, isInitialized, selectedChat]);

    const handleMessagesScroll = useCallback(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        const threshold = 100; // px от низа
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShouldAutoScroll(distanceFromBottom < threshold);
    }, []);

    // Устанавливаем флаг инициализации после первой загрузки
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 100); // Небольшая задержка для завершения первоначального рендера

        return () => clearTimeout(timer);
    }, []);

    // Cleanup при размонтировании компонента
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, []);

    // Проверяем активность кнопки протокола при изменении выбранного чата
    useEffect(() => {
        if (selectedChat && selectedChat.messages.length > 0) {
            setIsProtocolButtonActive(true);
        } else {
            setIsProtocolButtonActive(false);
        }
    }, [selectedChat]);

    // Проверяем наличие сгенерированных протоколов в текущем чате
    useEffect(() => {
        if (selectedChat && selectedChat.messages.length > 0) {
            const hasProtocols = selectedChat.messages.some((msg: MessageProps) => msg.type === 'json');
            setHasGeneratedProtocols(hasProtocols);
        } else {
            setHasGeneratedProtocols(false);
        }
    }, [selectedChat]);



    const handleSendMessage = useCallback(async (segmentsToSend?: { text: string, speakerId: string }[]) => {
        // Проверяем, была ли запись отменена для Azure
        if (recognitionType !== 'qwen' && isAzureRecordingCancelledRef.current) {
            console.log('Запись была отменена, сообщение не отправляется');
            return;
        }

        const segments = segmentsToSend || recognizedSegments;
        console.log('handleSendMessage вызвана. inputValue:', inputValue.trim(), 'segments:', segments.length, 'переданные сегменты:', !!segmentsToSend);

        let newMessage: MessageProps;
        let chatTitle: string;

        if (inputValue.trim()) {
            // Текстовое сообщение
            newMessage = {
                sender: 'user',
                time: getCurrentISOString(),
                type: 'text',
                text: inputValue,
                specialty: user?.specialty || '',
            };
            chatTitle = inputValue.substring(0, 30) + (inputValue.length > 30 ? '...' : '');
        } else if (segments.length > 0) {
            // Голосовое сообщение
            const textWithSpeakers = segments.map(seg => `${seg.speakerId || 'Unknown'}: ${seg.text}`).join('\n');
            newMessage = {
                sender: 'user',
                time: getCurrentISOString(),
                type: 'text',
                text: textWithSpeakers,
                title: t('dialogs.recognizedTitle'),
            };
            chatTitle = textWithSpeakers.substring(0, 30) + (textWithSpeakers.length > 30 ? '...' : '');
        } else {
            // Нет данных для отправки
            setRecognizedSegments([]);
            return;
        }

        if (!selectedChatId) {
            // Создаем новый чат
            try {
                const newChat = await createChat(chatTitle, newMessage);
                if (newChat) {
                    // addChat и selectChat уже вызываются в createChatOnServer.fulfilled
                    setIsProtocolButtonActive(true);

                    // НЕ синхронизируем сообщение отдельно, так как оно уже создано с чатом
                    // Обновляем название чата на сервере, если оно отличается от созданного
                    if (isOnline && newChat.title !== chatTitle) {
                        try {
                            await updateChatTitleOnServer(newChat.id, chatTitle);
                        } catch (error) {
                            console.warn('Не удалось обновить название чата на сервере:', error);
                        }
                    }
                } else {
                    console.warn('Не удалось создать чат');
                }
            } catch (error) {
                console.error('Ошибка создания чата:', error);
            }
        } else {
            // Отправляем в существующий чат
            const isFirstMessage = selectedChat && selectedChat.messages.length === 0;

            if (isOnline) {
                try {
                    await syncMessage(selectedChatId, newMessage);
                    setIsProtocolButtonActive(true);

                    // Обновляем заголовок чата на сервере, если это первое сообщение
                    if (isFirstMessage) {
                        try {
                            await updateChatTitleOnServer(selectedChatId, chatTitle);
                        } catch (error) {
                            console.warn('Не удалось обновить название чата на сервере:', error);
                        }
                    }
                } catch (error) {
                    console.warn('Не удалось синхронизировать сообщение:', error);
                    // Если не удалось отправить на сервер, добавляем локально
                    dispatch(addMessageToChat({ chatId: selectedChatId, message: newMessage }));
                    setIsProtocolButtonActive(true);
                }
            } else {
                // Если офлайн, добавляем локально
                dispatch(addMessageToChat({ chatId: selectedChatId, message: newMessage }));
                setIsProtocolButtonActive(true);
            }
        }

        // Очищаем состояние
        if (inputValue.trim()) {
            setInputValue('');
            const textarea = document.querySelector('textarea');
            if (textarea) {
                textarea.style.height = 'auto';
            }
        } else {
            setRecognizedSegments([]);
        }
    }, [inputValue, selectedChatId, user?.specialty, recognitionType, dispatch, isOnline, syncMessage, selectedChat, createChat, updateChatTitleOnServer, recognizedSegments, t]);



    const fetchSpecialities = async () => {
        try {
            const data = await searchSpecialities();
            setAvailableSpecialities(data);
            return data;
        } catch (error) {
            enqueueSnackbar(t('dialogs.specialtiesError'), {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
            });
            setAvailableSpecialities([]);
            return [];
        }
    };

    const handleOpenSpecialityDialog = async () => {
        // Проверяем, является ли пользователь Public организацией
        if (user && hasProtocolsAccess(user)) {
            // Для Public организации показываем диалог выбора протокола
            setIsProtocolSelectionDialogOpen(true);
            return;
        }

        // Для не-Public организаций используем старый алгоритм
        // Проверяем переменную окружения для отключения диалога
        const disableDialog = process.env.REACT_APP_DISABLE_SPECIALITY_DIALOG === 'true';
        const defaultSpeciality = process.env.REACT_APP_DEFAULT_SPECIALITY || null;

        // Загружаем специальности для использования в дальнейшем
        const specialities = await fetchSpecialities();

        if (disableDialog) {
            // Если диалог отключен, сразу генерируем протокол
            // Для v2 API можно не передавать specialty_id, будет использована первая специальность пользователя
            if (defaultSpeciality) {
                setChoosenProtocol(defaultSpeciality);
                // Пытаемся найти ID специальности по имени
                const specialityObj = specialities.find(s => s.name === defaultSpeciality);
                const specialityId = specialityObj?.id;
                setTimeout(() => {
                    handleGenerateProtocol(specialityId);
                }, 0);
            } else {
                // Если дефолтная специальность не указана, используем undefined (первая специальность пользователя)
                setTimeout(() => {
                    handleGenerateProtocol(undefined);
                }, 0);
            }
            return;
        }

        if (specialities.length === 0) {
            enqueueSnackbar(t('dialogs.noSpecialties'), {
                variant: 'warning',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 5000
            });
            return;
        }

        if (specialities.length === 1) {
            setChoosenProtocol(specialities[0].name);
            setIsSpecialityDialogOpen(false);
            setTimeout(() => {
                handleGenerateProtocol(specialities[0].id);
            }, 0);
            return;
        }

        setIsSpecialityDialogOpen(true);
    };

    const handleGenerateProtocolWithInstruction = async (specialityId: number | undefined, template?: string) => {
        if (!isProtocolButtonActive || !selectedChat || isTranscribing) return;
        setIsGeneratingProtocol(true);

        try {
            console.log('Отправка чата на формирование протокола...', { specialityId, template });

            // Используем переменную окружения для промта или дефолтное значение
            const prompt = process.env.REACT_APP_PROMPT || "LD-App-Base";

            const requestData: GenerateProtocolFromChatRequest = {
                chat_id: parseInt(selectedChatId!),
                specialty_id: specialityId, // ID специальности (опционально)
                prompt: prompt,
                template: template // Передаем название шаблона протокола
            };

            console.log('Отправляемые данные:', requestData);

            // Отправляем запрос на генерацию протокола
            const response = await generateProtocolFromChat(requestData);

            if (response.success) {
                console.log('Запрос на генерацию протокола отправлен, message_id:', response.message_id);
                // Анимированно удаляем лоадер
                setTimeout(() => {
                }, 500);

                // Результат генерации придет через SSE/WebSocket
                // и будет обработан в useChatStream

            } else {
                throw new Error(response.error || t('dialogs.protocolError'));
            }

        } catch (error) {
            console.error('Ошибка при генерации протокола:', error);

            const errorMessage = error instanceof Error ? error.message : t('dialogs.protocolError');
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 5000
            });
        } finally {
            setIsGeneratingProtocol(false);
        }
    };

    const handleSelectSpeciality = (speciality: string) => {
        // Находим специальность по name и получаем её id
        const specialityObj = availableSpecialities.find(s => s.name === speciality);
        const specialityId = specialityObj?.id;

        setChoosenProtocol(speciality);
        setIsSpecialityDialogOpen(false);
        setTimeout(() => {
            handleGenerateProtocol(specialityId);
        }, 0);
    };

    const handleProtocolSelect = (protocol: UserProtocol) => {
        setSelectedProtocolForGeneration(protocol);
        setIsProtocolSelectionDialogOpen(false);

        // Для Public организации используем специальность из протокола
        // Но теперь нужно найти ID специальности по имени
        const specialityName = protocol.specialty || 'общая';
        const specialityObj = availableSpecialities.find(s => s.name === specialityName);
        const specialityId = specialityObj?.id;

        setChoosenProtocol(specialityName);

        setTimeout(() => {
            // Передаем название протокола в параметр template и ID специальности
            handleGenerateProtocolWithInstruction(specialityId, protocol.name);
        }, 0);
    };

    const handleCloseProtocolSelection = useCallback(() => {
        setIsProtocolSelectionDialogOpen(false);
        setSelectedProtocolForGeneration(null);
    }, []);

    const handleGenerateProtocol = async (specialityId: number | undefined) => {
        // Используем основную функцию без шаблона
        await handleGenerateProtocolWithInstruction(specialityId);
        setChoosenProtocol(null);
    };



    const getMicrophoneStream = async (): Promise<MediaStream> => {
        // Проверяем наличие устройств ввода звука
        if (navigator.mediaDevices?.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasAudioInput = devices.some(d => d.kind === 'audioinput');
            if (!hasAudioInput) {
                const err = new Error('NoAudioInputDevice');
                err.name = 'NotFoundError';
                throw err;
            }
        }

        const anyNav: any = navigator;
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            return navigator.mediaDevices.getUserMedia({ audio: true });
        }
        const legacy = anyNav.getUserMedia || anyNav.webkitGetUserMedia || anyNav.mozGetUserMedia || anyNav.msGetUserMedia;
        if (!legacy) {
            const err = new Error('getUserMedia not supported');
            err.name = 'NotFoundError';
            throw err;
        }
        return new Promise((resolve, reject) => {
            legacy.call(navigator, { audio: true }, resolve, reject);
        });
    };

    const handleMicrophoneError = (error: unknown) => {
        if (error instanceof Error) {
            if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                enqueueSnackbar(t('dialogs.micNoDevice'), { variant: 'warning', autoHideDuration: 5000 });
            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                enqueueSnackbar(t('dialogs.micPermissionDenied'), { variant: 'warning', autoHideDuration: 5000 });
            } else {
                enqueueSnackbar(t('dialogs.micError'), { variant: 'error', autoHideDuration: 5000 });
            }
        } else {
            enqueueSnackbar(t('dialogs.micError'), { variant: 'error', autoHideDuration: 5000 });
        }
    };

    const startRecording = async () => {
        try {
            if (recognitionType === 'qwen') {
                // Qwen: запись локально без стримингового распознавания
                setIsRecording(true);
                setRecordingSeconds(0);
                setRecognizedSegments([]);
                setInterimText('');
                recordedChunksRef.current = [];
                isRecordingCancelledRef.current = false;

                // Проверка поддержки MediaRecorder
                const MediaRecorderCtor: any = (window as any).MediaRecorder;
                if (!MediaRecorderCtor) {
                    console.warn('MediaRecorder не поддерживается. Открываем диалог выбора файла.');
                    setIsRecording(false);
                    setIsAudioUploadDialogOpen(true);
                    return;
                }

                const stream = await getMicrophoneStream();
                audioStreamRef.current = stream;

                // Подбор поддерживаемого mimeType
                const candidates = [
                    'audio/webm;codecs=opus',
                    'audio/webm',
                    'audio/ogg;codecs=opus',
                    'audio/ogg',
                    'audio/mp4' // некоторые мобильные браузеры
                ];
                const chosen = candidates.find(t => MediaRecorderCtor.isTypeSupported?.(t));
                const options: MediaRecorderOptions = chosen ? { mimeType: chosen } : {};
                const mediaRecorder = new MediaRecorderCtor(stream, options);

                mediaRecorder.ondataavailable = (e: BlobEvent) => {
                    if (e.data && e.data.size > 0) {
                        recordedChunksRef.current.push(e.data);
                    }
                };

                mediaRecorder.onstop = async () => {
                    // Проверяем, была ли запись отменена
                    if (isRecordingCancelledRef.current) {
                        // Очищаем данные и останавливаем трэки
                        recordedChunksRef.current = [];
                        try {
                            (mediaRecorder as any).stream?.getTracks?.().forEach((t: MediaStreamTrack) => t.stop());
                            audioStreamRef.current?.getTracks().forEach(track => track.stop());
                            audioStreamRef.current = null;
                        } catch { }
                        // Сбрасываем флаг отмены после обработки
                        isRecordingCancelledRef.current = false;
                        return;
                    }

                    try {
                        const outType = (chosen && typeof chosen === 'string') ? chosen.split(';')[0] : 'audio/webm';
                        const ext = outType.includes('ogg') ? 'ogg' : outType.includes('mp4') ? 'mp4' : 'webm';
                        const blob = new Blob(recordedChunksRef.current, { type: outType });
                        const file = new File([blob], `recording.${ext}`, { type: outType });

                        // Гарантируем наличие чата
                        let chatIdStr = selectedChatId;
                        if (!chatIdStr) {
                            const newChat = await createChat(t('dialogs.voiceMessage'));
                            chatIdStr = newChat?.id || null;
                        }
                        if (!chatIdStr) return;



                        setIsTranscribing(true);
                        await ChatApiService.uploadUserAudio({
                            chatId: parseInt(chatIdStr),
                            file,
                            specialty: user?.specialty || 'общая',
                            context: selectedChat ? selectedChat.title : undefined,
                        }, undefined);
                    } catch (e) {
                        console.error('Ошибка загрузки записи Qwen:', e);
                    } finally {
                        setIsTranscribing(false);
                        try {
                            (mediaRecorder as any).stream?.getTracks?.().forEach((t: MediaStreamTrack) => t.stop());
                            audioStreamRef.current?.getTracks().forEach(track => track.stop());
                            audioStreamRef.current = null;
                        } catch { }
                    }
                };

                mediaRecorderRef.current = mediaRecorder;
                mediaRecorder.start();
                recordingTimerRef.current = setInterval(() => {
                    setRecordingSeconds(prev => prev + 1);
                }, 1000);
                return;
            }

            setIsRecording(true);
            setRecordingSeconds(0);
            setIsTranscribing(true);
            setRecognizedSegments([]);
            setInterimText('');
            pendingSegmentsRef.current = [];
            savedSegmentsCountRef.current = 0;
            isAzureRecordingCancelledRef.current = false;

            // Получаем MediaStream для волновой формы
            try {
                const stream = await getMicrophoneStream();
                audioStreamRef.current = stream;
            } catch (error) {
                console.error('Ошибка получения доступа к микрофону:', error);
                handleMicrophoneError(error);
                setIsRecording(false);
                setIsTranscribing(false);
                return;
            }

            const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(AZURE_SPEECH_KEY, AZURE_SPEECH_REGION);
            speechConfig.speechRecognitionLanguage = 'ru-RU';
            speechConfig.setProperty(
                "SpeechServiceResponse_ConversationTranscriptionMode",
                'true'
            );
            speechConfig.setProperty(
                "SpeechServiceResponse_DiarizationMode",
                'RealTime'
            );
            const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
            const transcriber = new SpeechSDK.ConversationTranscriber(speechConfig, audioConfig);
            recognizerRef.current = transcriber;

            // Добавляем фразы из phrases.json
            const phraseList = SpeechSDK.PhraseListGrammar.fromRecognizer(transcriber);
            if (Array.isArray(phrases)) {
                phrases.forEach((phrase) => phraseList.addPhrase(phrase));
            }

            transcriber.transcribing = (s, e) => {
                if (e.result.text) {
                    setInterimText(e.result.text);
                    console.log('[Diarization] Interim:', e.result.text, 'Speaker:', e.result.speakerId);
                }
            };
            transcriber.transcribed = (s, e) => {
                if (e.result.text) {
                    setRecognizedSegments(prev => [...prev, { text: e.result.text, speakerId: e.result.speakerId }]);
                    console.log('[Diarization] Final:', e.result.text, 'Speaker:', e.result.speakerId);
                }
            };
            transcriber.sessionStopped = () => {
                console.log('Сессия транскрибации остановлена');
                setIsRecording(false);
                setIsTranscribing(false);
                setInterimText('');
                transcriber.close();
                recognizerRef.current = null;
                // Останавливаем аудио поток для волновой формы
                if (audioStreamRef.current) {
                    audioStreamRef.current.getTracks().forEach(track => track.stop());
                    audioStreamRef.current = null;
                }
            };
            transcriber.canceled = () => {
                setIsRecording(false);
                setIsTranscribing(false);
                setIsWaitingForFinalResults(false);
                setInterimText('');
                transcriber.close();
                recognizerRef.current = null;
                // Останавливаем аудио поток для волновой формы
                if (audioStreamRef.current) {
                    audioStreamRef.current.getTracks().forEach(track => track.stop());
                    audioStreamRef.current = null;
                }
            };
            transcriber.startTranscribingAsync();
            recordingTimerRef.current = setInterval(() => {
                setRecordingSeconds(prev => prev + 1);
            }, 1000);
        } catch (error) {
            setIsRecording(false);
            setIsTranscribing(false);
            console.error('Ошибка записи звука:', error);
            handleMicrophoneError(error);
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
        }
    };

    const stopRecording = useCallback(() => {
        if (recognitionType === 'qwen') {
            // Убеждаемся, что флаг отмены не установлен (нормальная остановка)
            isRecordingCancelledRef.current = false;
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            // Останавливаем аудио поток
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
        } else if (recognizerRef.current) {
            recognizerRef.current.stopTranscribingAsync(() => {
                recognizerRef.current?.close();
                recognizerRef.current = null;
            }, (err: string) => {
                console.error('Ошибка при остановке распознавания:', err);
                recognizerRef.current?.close();
                recognizerRef.current = null;
            });
            // Останавливаем аудио поток для волновой формы
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
        }

        setIsRecording(false);
        if (recognitionType !== 'qwen') {
            setIsTranscribing(false);
            // Сбрасываем флаг отмены для Azure при нормальной остановке
            isAzureRecordingCancelledRef.current = false;
        }
        setIsWaitingForFinalResults(false);
        setRecordingSeconds(0);
        setInterimText('');
        pendingSegmentsRef.current = [];
        savedSegmentsCountRef.current = 0;
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
        }
    }, [recognitionType]);

    const handleCancelRecording = useCallback(() => {
        if (recognitionType === 'qwen') {
            // Устанавливаем флаг отмены перед остановкой
            isRecordingCancelledRef.current = true;
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            // Останавливаем аудио поток
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
            // Флаг сбросится в onstop после обработки
        } else if (recognizerRef.current) {
            // Устанавливаем флаг отмены для Azure
            isAzureRecordingCancelledRef.current = true;
            recognizerRef.current.stopTranscribingAsync(() => {
                recognizerRef.current?.close();
                recognizerRef.current = null;
            }, (err: string) => {
                console.error('Ошибка при остановке распознавания:', err);
                recognizerRef.current?.close();
                recognizerRef.current = null;
            });
            // Останавливаем аудио поток для волновой формы
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
        }

        setIsRecording(false);
        setIsTranscribing(false);
        setIsWaitingForFinalResults(false);
        setRecordingSeconds(0);
        setRecognizedSegments([]);
        setInterimText('');
        pendingSegmentsRef.current = [];
        savedSegmentsCountRef.current = 0;
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
        }
    }, [recognitionType]);

    const handlePrimaryButtonClick = () => {
        // Защита от повторных нажатий
        if (isWaitingForFinalResults) {
            return;
        }

        // В режиме Qwen: пишем с микрофона через MediaRecorder (без стриминга)
        if (recognitionType === 'qwen') {
            if (inputValue.trim()) {
                handleSendMessage().catch(error => console.error('Ошибка отправки сообщения:', error));
                return;
            }
            if (isRecording) {
                stopRecording();
            } else {
                startRecording();
            }
            return;
        }

        // Если Azure: есть текст в input или запись НЕ идет и есть распознанные сегменты
        // Проверяем, что запись не была отменена
        if (inputValue.trim() || (!isRecording && recognizedSegments.length > 0 && !isAzureRecordingCancelledRef.current)) {
            handleSendMessage().catch(error => console.error('Ошибка отправки сообщения:', error));
        } else if (isRecording) {
            // Если идет запись, останавливаем ее и ждем финальные результаты
            console.log('Останавливаем запись для отправки сообщения. Текущие сегменты:', recognizedSegments.length);

            // Сохраняем текущие сегменты для отправки
            pendingSegmentsRef.current = [...recognizedSegments];
            savedSegmentsCountRef.current = recognizedSegments.length;
            setIsWaitingForFinalResults(true);
            stopRecording();

            // Ждем дополнительные финальные результаты
            setTimeout(() => {
                // Проверяем, была ли запись отменена
                if (isAzureRecordingCancelledRef.current) {
                    console.log('Запись была отменена, сообщение не отправляется');
                    pendingSegmentsRef.current = [];
                    savedSegmentsCountRef.current = 0;
                    setIsWaitingForFinalResults(false);
                    isAzureRecordingCancelledRef.current = false;
                    return;
                }

                // Берем только новые сегменты (после savedSegmentsCountRef.current)
                const newSegments = recognizedSegmentsRef.current.slice(savedSegmentsCountRef.current);
                const allSegments = [...pendingSegmentsRef.current, ...newSegments];
                console.log('Отправляем сообщение. Сохраненные сегменты:', pendingSegmentsRef.current.length, 'Новые сегменты:', newSegments.length, 'Всего:', allSegments.length);

                if (allSegments.length > 0) {
                    // Отправляем сообщение с объединенными сегментами
                    handleSendMessage(allSegments).catch(error => console.error('Ошибка отправки сообщения:', error));
                    // Очищаем после отправки
                    pendingSegmentsRef.current = [];
                    savedSegmentsCountRef.current = 0;
                    setIsWaitingForFinalResults(false);
                } else {
                    pendingSegmentsRef.current = [];
                    savedSegmentsCountRef.current = 0;
                    setIsWaitingForFinalResults(false);
                }
                // Сбрасываем флаг отмены после обработки
                isAzureRecordingCancelledRef.current = false;
            }, 1000); // Увеличиваем время для получения финальных результатов
        } else {
            // Начинаем запись
            startRecording();
        }
    };

    const formatTime = useCallback((seconds: number) => {
        const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${minutes}:${secs}`;
    }, []);



    // функция для открытия диалога загрузки аудио
    const handleAudioUpload = async () => {
        // Гарантируем, что есть чат для загрузки аудио
        if (!selectedChatId) {
            try {
                const newChat = await createChat(t('dialogs.newChat'));
                if (!newChat) {
                    console.warn('Не удалось создать чат для загрузки аудио');
                    return;
                }
            } catch (e) {
                console.error('Ошибка создания чата для загрузки аудио:', e);
                return;
            }
        }
        setIsAudioUploadDialogOpen(true);
    };

    // функция для обработки завершения транскрибации аудио
    const handleTranscriptionComplete = async (text: string) => {
        // text может быть "Ожидаем распознавание..." — используем как placeholder
        const placeholderMessage: MessageProps = {
            sender: 'user',
            time: getCurrentISOString(),
            type: 'text',
            text: t('dialogs.waitingRecognition'),
            title: t('dialogs.recognizedTitle'),
        };

        if (!selectedChatId) {
            // Создаем чат и кладем placeholder как первое локальное сообщение
            const chatTitle = t('dialogs.voiceMessage');
            try {
                const newChat = await createChat(chatTitle, placeholderMessage);
                if (newChat) {
                    setIsProtocolButtonActive(true);
                } else {
                    console.warn('Не удалось создать чат');
                }
            } catch (error) {
                console.error('Ошибка создания чата:', error);
            }
        } else {
            // В существующий чат добавляем локально placeholder, БЕЗ синхронизации
            dispatch(addMessageToChat({ chatId: selectedChatId, message: placeholderMessage }));
            setIsProtocolButtonActive(true);
        }
    };

    const handleSendToMISWithProtocol = useCallback(async (protocolData: ProtocolMessage, protocolId: string) => {
        try {
            const requestData = {
                protocolId: protocolId,
                message: JSON.stringify(protocolData)
            };
            const response = await apiPost('/mis/extract-fields', requestData);
            const responseData = await response.json();
            if (response.ok && responseData.request) {
                const fields = responseData.request.fields?.map((field: any) => ({
                    fieldId: field.fieldId,
                    fieldValue: field.fieldValue
                })) || [];
                window.parent.postMessage(
                    {
                        event: "protocolFields",
                        payload: {
                            protocolId: parseInt(responseData.request.protocolId) || parseInt(protocolId),
                            fields: fields
                        }
                    },
                    "*"
                );
                enqueueSnackbar(t('dialogs.misSuccess'), {
                    variant: 'success',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                    autoHideDuration: 3000
                });
            } else {
                throw new Error(responseData.message || t('dialogs.misError'));
            }
        } catch (error) {
            console.error('Ошибка при отправке в МИС:', error);
            enqueueSnackbar(`${t('dialogs.misError')}: ${error instanceof Error ? error.message : ''}`, {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 5000
            });
        }
    }, [enqueueSnackbar, t]);

    const handleOpenInstrumentsFromMessage = useCallback(
        (protocol: ProtocolMessage, serverId?: number, anchor?: { top: number; left: number }) => {
            setInstrumentsDialogProtocol(protocol);
            setInstrumentsDialogProtocolId(urlProtocolId || serverId?.toString() || Date.now().toString());
            setInstrumentsDialogAnchor(anchor ?? null);
            setInstrumentsDialogOpen(true);
        },
        [urlProtocolId]
    );

    const handleCopyProtocol = useCallback((protocol: ProtocolMessage) => {
        navigator.clipboard.writeText(JSON.stringify(protocol)).then(() => {
            enqueueSnackbar(t('dialogs.protocolCopied'), {
                variant: 'success',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 2000
            });
        }).catch(() => {
            enqueueSnackbar(t('dialogs.copyFailed'), {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 2000
            });
        });
    }, [enqueueSnackbar, t]);

    const handleExportPdf = useCallback(async (protocol: ProtocolMessage, protocolId: string) => {
        try {
            const response = await apiPost('/protocols/generate-pdf', {
                message: JSON.stringify(protocol),
            });

            if (!response.ok) {
                let errorMessage = t('dialogs.pdfError');
                try {
                    const errorData = await response.json();
                    if (errorData?.detail) {
                        errorMessage = errorData.detail;
                    }
                } catch {
                    // ignore parse error, use default message
                }

                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                    autoHideDuration: 5000,
                });
                return;
            }

            const blob = await response.blob();
            const disposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
            let fileName = `protocol-${protocolId || Date.now().toString()}.pdf`;

            if (disposition) {
                const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
                const simpleMatch = /filename="?([^";]+)"?/i.exec(disposition);
                const rawName = (utf8Match && utf8Match[1]) || (simpleMatch && simpleMatch[1]);
                if (rawName) {
                    try {
                        fileName = decodeURIComponent(rawName);
                    } catch {
                        fileName = rawName;
                    }
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Ошибка генерации PDF файла:', error);
            enqueueSnackbar(t('dialogs.pdfError'), {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 5000,
            });
        }
    }, [enqueueSnackbar, t]);

    const handleExportDocx = useCallback(async (protocol: ProtocolMessage, protocolId: string) => {
        try {
            const response = await apiPost('/protocols/generate-docx', {
                message: JSON.stringify(protocol),
            });

            if (!response.ok) {
                let errorMessage = t('dialogs.docxError');
                try {
                    const errorData = await response.json();
                    if (errorData?.detail) {
                        errorMessage = errorData.detail;
                    }
                } catch {
                    // ignore parse error, use default message
                }

                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                    autoHideDuration: 5000,
                });
                return;
            }

            const blob = await response.blob();
            const disposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
            let fileName = `protocol-${protocolId || Date.now().toString()}.docx`;

            if (disposition) {
                const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
                const simpleMatch = /filename="?([^";]+)"?/i.exec(disposition);
                const rawName = (utf8Match && utf8Match[1]) || (simpleMatch && simpleMatch[1]);
                if (rawName) {
                    try {
                        fileName = decodeURIComponent(rawName);
                    } catch {
                        fileName = rawName;
                    }
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Ошибка генерации DOCX файла:', error);
            enqueueSnackbar(t('dialogs.docxError'), {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 5000,
            });
        }
    }, [enqueueSnackbar, t]);

    // Обработчик для кнопки "Передать в МИС" в ChatInput (последний протокол в чате)
    const handleSendToMIS = useCallback(async () => {
        if (!selectedChat || !hasGeneratedProtocols) {
            console.warn('Нет сгенерированных протоколов для отправки');
            return;
        }
        const lastProtocolMessage = [...selectedChat.messages]
            .reverse()
            .find((msg: MessageProps) => msg.type === 'json');
        if (lastProtocolMessage && lastProtocolMessage.json) {
            const protocolId = urlProtocolId || lastProtocolMessage.serverId?.toString() || Date.now().toString();
            await handleSendToMISWithProtocol(lastProtocolMessage.json, protocolId);
        } else {
            enqueueSnackbar(t('dialogs.protocolNotFound'), {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                autoHideDuration: 3000
            });
        }
    }, [selectedChat, hasGeneratedProtocols, handleSendToMISWithProtocol, enqueueSnackbar, t, urlProtocolId]);

    return <div className="dialogs-page-content" id="dialogs-container">
        <ChatHeader
            title={selectedChat ? selectedChat.title : t('dialogs.newChat')}
            chatId={selectedChat?.id}
            isOnline={isOnline}
            isSyncing={isSyncing}
            isConnected={isConnected}
            onTitleClick={(chatId) => {
                if (selectedChat) {
                    openRenameDialog(chatId, selectedChat.title);
                }
            }}
        />


        {selectedChat ? (
            <MessagesList
                messages={selectedChat.messages}
                messagesEndRef={messagesEndRef}
                containerRef={messagesContainerRef}
                onScroll={handleMessagesScroll}
                onOpenInstruments={handleOpenInstrumentsFromMessage}
                onCopyProtocol={handleCopyProtocol}
                isGeneratingProtocol={isGeneratingProtocol}
            />
        ) : (
            <div className="no-chat-selected">
                <p>{t('dialogs.selectOrStart')}</p>
            </div>
        )}
        <ChatInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            isRecording={isRecording}
            recordingSeconds={recordingSeconds}
            isTranscribing={isTranscribing}
            isGeneratingProtocol={isGeneratingProtocol}
            isProtocolButtonActive={isProtocolButtonActive}
            isWaitingForFinalResults={isWaitingForFinalResults}
            hasGeneratedProtocols={hasGeneratedProtocols}
            onInput={handleInput}
            onSend={handleSendMessage}
            onPrimaryButtonClick={handlePrimaryButtonClick}
            onCancelRecording={handleCancelRecording}
            onGenerateProtocol={handleOpenSpecialityDialog}
            onAudioUpload={handleAudioUpload}
            onSendToMIS={handleSendToMIS}
            formatTime={formatTime}
            lastRecognizedText={recognizedSegments.map(seg => seg.text).join(' ')}
            interimText={interimText}
            audioStream={audioStreamRef.current}
        />
        {process.env.REACT_APP_DISABLE_SPECIALITY_DIALOG !== 'true' && (
            <SpecialityDialog
                isOpen={isSpecialityDialogOpen}
                onClose={() => setIsSpecialityDialogOpen(false)}
                onSelect={handleSelectSpeciality}
                specialities={availableSpecialities}
            />
        )}
        <AudioUploadDialog
            isOpen={isAudioUploadDialogOpen}
            onClose={() => setIsAudioUploadDialogOpen(false)}
            onTranscriptionComplete={handleTranscriptionComplete}
            isTranscribing={isTranscribing}
            setIsTranscribing={setIsTranscribing}
            chatId={selectedChatId}
            specialty={user?.specialty || 'общая'}
            contextHint={selectedChat ? selectedChat.title : ''}
        />
        <ProtocolSelectionDialog
            isOpen={isProtocolSelectionDialogOpen}
            onClose={handleCloseProtocolSelection}
            onProtocolSelect={handleProtocolSelect}
            isLoading={isGeneratingProtocol}
        />
        {renameChatId && (
            <RenameChatDialog
                isOpen={isRenameDialogOpen}
                onClose={closeRenameDialog}
                chatId={renameChatId}
                currentTitle={renameChatTitle}
            />
        )}
        <ProtocolInstrumentsDialog
            isOpen={instrumentsDialogOpen}
            onClose={() => {
                setInstrumentsDialogOpen(false);
                setInstrumentsDialogProtocol(null);
                setInstrumentsDialogProtocolId('');
                setInstrumentsDialogAnchor(null);
            }}
            protocol={instrumentsDialogProtocol}
            protocolId={instrumentsDialogProtocolId}
            anchor={instrumentsDialogAnchor}
            onSendToMIS={handleSendToMISWithProtocol}
            onExportPdf={handleExportPdf}
            onExportDocx={handleExportDocx}
        />
    </div>;
};

export default DialogsPage;
